/**
 * A tab left open across a deploy asks for hashed chunks the new build deleted.
 * When a lazy part fails, this finds out whether the host now serves another entry
 * script and, if so, reloads at a moment that costs the learner nothing.
 */

/** The first `<script type="module" src>` of an HTML page: Bun's build puts the entry chunk there. */
export function entrySrc(html: string): string | null {
	for (const tag of html.match(/<script\b[^>]*>/gi) ?? []) {
		if (!/\btype\s*=\s*["']?module\b/i.test(tag)) continue;
		const src = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag);
		const value = src?.[1] ?? src?.[2] ?? src?.[3];
		if (value) return value;
	}
	return null;
}

/** Unknown on either side is not stale: a check that cannot tell must not reload. */
export const isStale = (running: string | null, served: string | null): boolean =>
	running !== null && served !== null && running !== served;

const GUARD_KEY = "aybuben:stale-build-target";

export interface StaleBuildDeps {
	fetchHtml: () => Promise<string>;
	runningSrc: () => string | null;
	online: () => boolean;
	/** A getter: touching `sessionStorage` can throw (private mode). Null when there is none. */
	storage: () => Pick<Storage, "getItem" | "setItem"> | null;
	reload: () => void;
	/** True while a lesson (or what follows it) owns the screen. */
	busy: () => boolean;
	/** Calls `fn` each time the tab becomes hidden. Returns an unsubscribe. */
	onHidden: (fn: () => void) => () => void;
}

export interface StaleBuild {
	/** Whether the host serves a newer build; one fetch per page. Arms the reload when it does. */
	check: () => Promise<boolean>;
}

export function createStaleBuild(deps: StaleBuildDeps): StaleBuild {
	let checking: Promise<boolean> | null = null;

	const guardRead = (): string | null | undefined => {
		try {
			return deps.storage()?.getItem(GUARD_KEY);
		} catch {
			return undefined;
		}
	};
	const guardWrite = (target: string): boolean => {
		try {
			const s = deps.storage();
			if (!s) return false;
			s.setItem(GUARD_KEY, target);
			return true;
		} catch {
			return false;
		}
	};

	let reloading = false;
	const arm = (target: string) => {
		const off = deps.onHidden(() => {
			if (reloading || deps.busy()) return;
			// Written before the reload: a page that still runs another entry afterwards
			// finds its target recorded and stays put, so a bad deploy cannot loop.
			if (!guardWrite(target)) return;
			reloading = true;
			off();
			deps.reload();
		});
	};

	const run = async (): Promise<boolean> => {
		let served: string | null;
		try {
			served = entrySrc(await deps.fetchHtml());
		} catch {
			// Retryable by a later failure: the network may be back.
			checking = null;
			return false;
		}
		const stale = isStale(deps.runningSrc(), served);
		// A clean "not stale" is only true until the next deploy: let a later failure ask again.
		if (!stale) checking = null;
		// `served` is non-null whenever `stale` is.
		if (stale && served !== null) {
			const guard = guardRead();
			// undefined: storage unavailable, null: never reloaded. Only null may reload.
			if (guard === null) arm(served);
		}
		return stale;
	};

	return {
		check: () => {
			if (!deps.online()) return Promise.resolve(false);
			return (checking ??= run());
		},
	};
}

/** The served page's HTML; a non-ok answer (a 5xx page) is a failed check, not "not stale". */
export async function fetchServedHtml(
	fetcher: (url: string, init: RequestInit) => Promise<Pick<Response, "ok" | "status" | "text">>,
): Promise<string> {
	const r = await fetcher("/", { cache: "no-store" });
	if (!r.ok) throw new Error(`index.html answered ${r.status}`);
	return r.text();
}

export interface PartReportsDeps {
	check: () => Promise<boolean>;
	online: () => boolean;
}

export interface PartReportTags {
	lazy_part: string;
	online: boolean;
	stale_build: boolean;
}

/** Reports each lazy part's failure once per page, tagged with whether the build is stale. */
export function createPartReports(deps: PartReportsDeps) {
	const reported = new Set<string>();
	return {
		async report(
			part: string,
			error: unknown,
			capture: (error: unknown, tags: PartReportTags) => void,
		): Promise<void> {
			// Claimed before the await: two slots failing in the same tick send one.
			if (reported.has(part)) return;
			reported.add(part);
			const online = deps.online();
			const stale = online ? await deps.check().catch(() => false) : false;
			capture(error, { lazy_part: part, online, stale_build: stale });
		},
	};
}

/** Lets AppLayout say when a lesson is open, without the module importing its state. */
let busyNow: () => boolean = () => false;
export const setBusy = (fn: () => boolean): void => {
	busyNow = fn;
};

const production = process.env.NODE_ENV === "production";

const browserStale: StaleBuild | null = production
	? createStaleBuild({
			fetchHtml: () => fetchServedHtml(fetch),
			runningSrc: () =>
				document.querySelector('script[type="module"][src]')?.getAttribute("src") ?? null,
			online: () => navigator.onLine,
			storage: () => sessionStorage,
			reload: () => location.reload(),
			busy: () => busyNow(),
			onHidden: (fn) => {
				const on = () => {
					if (document.visibilityState === "hidden") fn();
				};
				document.addEventListener("visibilitychange", on);
				return () => document.removeEventListener("visibilitychange", on);
			},
		})
	: null;

/** Dev and tests never fetch `/` from here. */
export const partReports = createPartReports({
	check: () => browserStale?.check() ?? Promise.resolve(false),
	online: () => navigator.onLine,
});
